/**
 * Cost of the query plans within one executed batch - the "Query cost (relative to the batch)"
 * header known from SSMS (and v1). Each plan XML carries the estimated cost of its statement
 * in the StatementSubTreeCost attribute; the share of a plan is its cost over the sum of all
 * plans of the batch.
 */

export type QueryPlanCosts = {
	/** estimated cost of every plan (0 when the plan has no cost) */
	costs: number[],
	/** sum of the costs */
	total: number,
	/** the most expensive plan */
	max: number
};

/**
 * Estimated cost of a plan - the StatementSubTreeCost of its (first) statement.
 */
export function getQueryPlanCost(xml: string | null | undefined): number {
	if (!xml) return 0;
	const match = /StatementSubTreeCost="([^"]*)"/.exec(xml);
	const cost = match ? parseFloat(match[1]) : NaN;
	return Number.isFinite(cost) && cost > 0 ? cost : 0;
}

export function computeQueryPlanCosts(xmls: (string | null | undefined)[]): QueryPlanCosts {
	const costs = xmls.map(getQueryPlanCost);
	return {
		costs,
		total: costs.reduce((sum, cost) => sum + cost, 0),
		max: costs.reduce((max, cost) => Math.max(max, cost), 0)
	};
}

/**
 * Share of the plan on the whole batch, 0..1 (0 when the batch has no cost at all).
 */
export function getQueryPlanShare(cost: number, total: number): number {
	return total > 0 ? cost / total : 0;
}

export function formatQueryPlanShare(cost: number, total: number, locale?: string): string {
	const share = getQueryPlanShare(cost, total);
	try {
		return new Intl.NumberFormat(locale || undefined, {style: 'percent', maximumFractionDigits: 0}).format(share);
	} catch {
		return `${Math.round(share * 100)}%`;
	}
}

/**
 * Header color of the plan: green for the cheap plans through yellow to red for the most
 * expensive plan of the batch (the same idea as the red-green gradient of v1).
 */
export function getQueryPlanCostColor(cost: number, max: number): string {
	const ratio = max > 0 ? Math.min(1, Math.max(0, cost / max)) : 0;
	const hue = Math.round(120 * (1 - ratio));
	return `hsl(${hue}, 55%, 38%)`;
}
